import { defineStore, acceptHMRUpdate } from 'pinia';
import { useActorStore } from '@/stores/actors';
import * as api from '@/api';
import {
  doLoading,
  addUpdateRecord,
  clearEmpty,
  isExisting,
  removeRecord, buildChanges,
} from '@/utilities/helpers.js';

export const useSkillStore = defineStore('skills', {
  state: () => ({
    /** @type {number} */
    loading: 0,
    /** @type {{id: string, skill: string, skillCategoryId: string}[]} */
    records: [],
    /** @type {{id: string, category: string}[]} */
    categories: [],
  }),
  getters: {
    count: (state) => state.records.length,
    retrieve: (state) => (skillId) => state.records.find((v) => v.id === skillId),
    isLoading: (state) => state.loading > 0,
  },
  actions: {
    forCategory(category) {
      const categoryId = this.categories.find(v => v.category === category)?.id;
      return this.forCategoryId(categoryId);
    },
    forCategoryId(categoryId) {
      return this.records.filter((v) => v.skillCategoryId === categoryId);
    },
    forActorId(actorId) {
      const actor = useActorStore().retrieve(actorId);
      return actor.skillIds.map((skillId) => this.retrieve(skillId) || {});
    },
    async processRecord(record) {
      this.processCategoryRecord({
        id: record.skillCategoryId,
        category: record.category,
      });
      return addUpdateRecord(this.records, record);
    },
    async processCategoryRecord(record) {
      return addUpdateRecord(this.categories, record);
    },
    async processRecords(records) {
      if (records) {
        for (const record of records) {
          await this.processRecord(record);
        }
      }
    },
    async removeRecord(skillId) {
      removeRecord(this.records, skillId);
    },
    async removeRecordsBy({ category }) {
      if (category) {
        const toRemove = this.forCategory(category).map((e) => e.id);
        for (const toRemoveId of toRemove) {
          await this.removeRecord(toRemoveId);
        }
      }
    },
    async syncCategories() {
      await doLoading(this, async () => {
        await api.utils.paginateAll({
          fetch: api.skills.listCategories,
          node: 'skillCategories',
          filters: { limit: 100 },
        }, async (edge) => {
          await this.processCategoryRecord(edge.node);
        });
      });
    },
    async sync() {
      await doLoading(this, async () => {
        await api.utils.paginateAll({
          fetch: api.skills.list,
          node: 'skills',
          filters: { limit: 100 },
        }, async (edge) => {
          await this.processRecord(edge.node);
        });
      });
    },
    async save(skill) {
      if (isExisting(skill.id)) {
        // existing
        const original = this.retrieve(skill.id);
        const payload = {
          skillId: skill.id,
          updates: buildChanges(original, skill, { skip: ['id', 'updatedAt'] }),
        };

        return await doLoading(this, async () => {
          if (Object.keys(payload.updates).length > 0) {
            return await api.skills.update(payload).then(async (data) => {
              return this.processRecord(data.skill);
            });
          } else {
            return original;
          }
        });
      } else {
        // new skill
        const prepared = {
          skill: clearEmpty(skill),
        };
        return await doLoading(this, async () => {
          return await api.skills.create(prepared).then(async (data) => {
            return this.processRecord(data.skill);
          });
        });
      }
    },
    async delete(skillId) {
      const skill = this.retrieve(skillId);
      if (!skill) {
        return;
      }
      const payload = { skillId };

      return await doLoading(this, async () => {
        return await api.skills.delete(payload).then(async data => {
          await this.removeRecord(data.skillId);
          return data.skillId;
        });
      });
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSkillStore, import.meta.hot));
}
